<template>
  <div class="god_page_container">
    <h2>Monedero</h2>
    <div class="filters-container">
      <input type="text" placeholder="Filtrar por email" v-model="filterText" class="input-text"/>
      <button class="main_button" @click="addMovement()">
        Añadir movimiento
        <img class="action_icon_white" src="@/assets/images/ic_add.png" />
      </button>
    </div>
    <div class="table-container">
      <table>
        <thead>
          <tr>
            <th class="big">Usuario</th>
            <th class="big">Descripción</th>
            <th class="small">Cantidad pendiente</th>
            <th class="small">Fecha caducidad</th>
            <th class="small">Fecha creación</th>
            <th class="small">Estado</th>
            <th class="small">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(item, index) in movements
              .filter(movement => !filterText ||
                (movement.user_name && movement.user_name.toLowerCase().includes(filterText.toLowerCase())))"
            :key="item.id"
            :class="index % 2 === 0 ? 'bg-gray-100' : 'bg-white'"
          >
            <td>{{ item.user_name }}</td>
            <td>{{ item.title }}</td>
            <td>{{ formatAmount(item.pending_amount) }}€ de {{ formatAmount(item.amount) }}€</td>
            <td>{{ item.expiration_date === null ? 'No caduca' : item.expiration_date }}</td>
            <td>{{ item.movement_date }}</td>
            <td>{{ item.status }}</td>
            <td>
              <img class="action_icon" src="@/assets/images/ic_action_edit.png" @click="onEditClicked(item)"/>
              <img class="action_icon" src="@/assets/images/ic_wallet_delete.png" @click="onDeleteClicked(item)"/>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <WalletMovementPopup
      :show-movement-popup="showMovementPopup"
      :movement="selectedMovement"
      @movementSaved="onMovementSaved"
      @movementPopupCancelled="movementPopupCancelled"
      @errorHappened="errorHappened"
      />

    <TwoButtonPopup 
      :title="deleteMovementTitle"
      :message="deleteMovementMessage"
      acceptButton="Borrar movimiento"
      @acceptCallback="() => deleteMovementConfirmed(movementToDelete)"
      cancelButton="Cancelar"
      @cancelCallback="deleteMovementCancelled"
      />

    <ErrorPopup :errorMessage="error" />

  </div>

</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";
import { formatAmount } from '@/utils/helpers';

import ErrorPopup from "@/components/common/ErrorPopup.vue";
import TwoButtonPopup from "@/components/common/TwoButtonPopup.vue";
import WalletMovementPopup from "@/components/wallet/WalletMovementPopup.vue";

const error = ref(null);
const movements = ref([]);
const loadingStore = useLoadingStore();
const selectedPurchaseData = ref(null);
const router = useRouter();
const route = useRoute();
const filterText = ref(route.query.filterText || "");

watch(filterText, () => {
  router.replace({
    query: {
      ...route.query,
      filterText: filterText.value
    }
  });
});

const showMovementPopup = ref(false);
const selectedMovement = ref(null);

const deleteMovementTitle = ref("");
const deleteMovementMessage = ref("");
const movementToDelete = ref(null);

const getWalletMovements = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await fetchWalletMovements();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await fetchWalletMovements();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      showError("Se ha producido un error obteniendo los movimientos.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
      movements.value = [];
    } else {
      error.value = null;
      movements.value = data.movements;
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
};

async function fetchWalletMovements() {
  return await fetch(process.env.VUE_APP_API_URL+"api/god/wallet/get?user_id="+localStorage.getItem('user_id'), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
    });
}

getWalletMovements();

const handlePopState = () => {
  selectedPurchaseData.value = null;
};

onMounted(() => {
  window.addEventListener("popstate", handlePopState);
});

onUnmounted(() => {
  window.removeEventListener("popstate", handlePopState);
});

const onEditClicked = (item) => {
  selectedMovement.value = item;
  showMovementPopup.value = true;
};

const onMovementSaved = () => {
  showMovementPopup.value = false;
  getWalletMovements();
};

const movementPopupCancelled = () => {
  showMovementPopup.value = false;
}

const errorHappened = (message) => {
  showError(message);
}

const onDeleteClicked = (item) => {
  deleteMovementTitle.value = "Borrar movimiento";
  deleteMovementMessage.value = "¿Estás seguro de que deseas borrar este movimiento de "+item.user_name+"?";
  movementToDelete.value = item;
};

const showError = async (message) => {
  error.value = null;
  await nextTick(); // Espera a que Vue reaccione al cambio
  error.value = message;
};

const deleteMovementConfirmed = () => {
  if (movementToDelete.value) {
    deleteMovementTitle.value = null;
    deleteMovement();
  }
};

const deleteMovementCancelled = () => {
  deleteMovementTitle.value = null;
  movementToDelete.value = null;
};

const deleteMovement = async () => {
  loadingStore.setLoading(true);
  error.value = null;

  try {
    let response = await callAPIToDeleteMovement();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await callAPIToDeleteMovement();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    movementToDelete.value = null;

    if (!response.ok) {
      showError("Se ha producido un error eliminando el movimiento.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      error.value = data.message;
    } else {
      getWalletMovements();
    }
  } catch (exception) {
    error.value = exception.message;
  } finally {
    loadingStore.setLoading(false);
  }
};

async function callAPIToDeleteMovement() {
  return await fetch(process.env.VUE_APP_API_URL+"api/god/wallet/delete", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vgod": process.env.VUE_APP_API_VGOD,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        "user_id": localStorage.getItem('user_id'),
        "movement_id": movementToDelete.value.id,
      }),
    });
}

async function addMovement() {
  selectedMovement.value = null;
  showMovementPopup.value = true;
}

onMounted(() => {
  // filterText.value = route.query.user_email !== undefined ? route.query.user_email : ""; // Remove old logic that overwrote filterText with user_email, unless that was intended behavior. The user asked for state persistence.
  // The original code had: filterText.value = route.query.user_email ...
  // This likely meant "if I come from Users page clicking Wallet, filter by that user".
  // My new logic: `filterText = ref(route.query.filterText || "")`.
  // If I want to support BOTH "coming from Users page" AND "persisting my own filter":
  // The "coming from Users page" logic puts `user_email` in query.
  // So validation:
  if (route.query.user_email) {
     filterText.value = route.query.user_email;
  }
});

</script>

<style scoped>

.table-container {
  overflow-x: auto;
  width: 100%;
}

table {
  border-radius: 10px;
  overflow: hidden;
  margin-top: 30px;
  width: 100%;
  border-collapse: collapse;
}

th {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  background-color: var(--gray);
  color: white;
  border: 0px;
}

.big {
  width: 25%;
}

.small {
  width: 10%;
}

tr{
  cursor: pointer;
}

td {
  text-align: center;
  padding-top: 10px;
  padding-bottom: 10px;
  padding-left: 5px;
  padding-right: 5px;
}

.bg-gray-100 {
  background-color: var(--almost-white);
}

.bg-white {
  background-color: white;
}

.action_icon {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin: 0px 5px;
}

.action_icon_white {
  width: 20px;
  height: 20px;
  cursor: pointer;
  margin-left: 10px;
  filter: brightness(0) invert(1);
}

.filters-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
}

.filters-container .input-text {
  width: 300px;
}

.main_button {
  width: auto;
  padding-left: 20px;
  padding-right: 20px;
}

@media (max-width: 768px) {
  table {
    width: 100%;
    min-width: 0px;
  }
  
  .filters-container {
    flex-direction: column;
    gap: 10px;
    align-items: stretch;
  }

  .filters-container .input-text {
    width: 100%;
  }
  
  .main_button {
    width: 100%;
  }
}

</style>
